import { useState } from 'react';

import { useGraphQLMutation } from '@betterment/graphql';
import { Box, Button, Dialog, H2, Icon, ListRow, Separator, Text } from '@betterment/samba-react';
import { useColorMode } from '@betterment/shared-components/src/components/color-mode/ColorModeProvider';
import { DragDropContext, Draggable, DropResult, Droppable } from '@hello-pangea/dnd';

import { getEnvelopeAccountName, getRowSubtext } from './components/AccountRow';
import { LeadingIcon } from './components/LeadingIcon';
import { UpdateEnvelopeOrdersDocument } from './graphql/mutation.updateEnvelopeOrder.generated';
import { EnvelopesCardQuery } from './graphql/query.envelopesCard.generated';

import { EnvelopeOrderInput } from '../../graphql/schema';

type Envelope = EnvelopesCardQuery['envelopes'][number];

const DraggableEnvelopeRow: React.FC<{ envelope: Envelope }> = ({ envelope }) => {
  const { colorMode } = useColorMode();
  const { accounts, accountsDescription, purpose } = envelope;

  if (!purpose) {
    return (
      <>
        {accounts.map(account => {
          if (account.__typename === 'CheckingAccount') {
            return null;
          }

          return (
            <ListRow key={account.id}>
              <ListRow.LeadingAsset>
                <LeadingIcon url={account.icon[colorMode]} />
              </ListRow.LeadingAsset>
              <ListRow.LeadingContent>
                <Text>{getEnvelopeAccountName(account)}</Text>
                <Text variant="text50" color="text.weak">
                  {getRowSubtext(account)}
                </Text>
              </ListRow.LeadingContent>
              <ListRow.TrailingContent>
                <Icon name="DragIndicator" size="small" />
              </ListRow.TrailingContent>
            </ListRow>
          );
        })}
      </>
    );
  }

  return (
    <ListRow>
      <ListRow.LeadingAsset>
        <LeadingIcon url={purpose.image[colorMode]} />
      </ListRow.LeadingAsset>
      <ListRow.LeadingContent>
        <Text>{purpose.name}</Text>
        {accountsDescription && (
          <Text variant="text50" color="text.weak">
            {accountsDescription}
          </Text>
        )}
      </ListRow.LeadingContent>
      <ListRow.TrailingContent>
        <Icon name="DragIndicator" size="small" />
      </ListRow.TrailingContent>
    </ListRow>
  );
};

export const EnvelopesReorderDialog: React.FC<{ envelopes: Envelope[] }> = ({ envelopes }) => {
  const [orderedEnvelopes, setOrderedEnvelopes] = useState(
    envelopes.filter(envelope => !envelope.accounts.some(account => account.__typename === 'CheckingAccount'))
  );
  const [updateEnvelopeOrder] = useGraphQLMutation(UpdateEnvelopeOrdersDocument);

  const handleDragEnd = (result: DropResult) => {
    const { destination, source } = result;

    // Dropped outside the list
    if (!destination) {
      return;
    }

    // No position change
    if (destination.index === source.index) {
      return;
    }

    // Reorder the array
    const reorderedEnvelopes = Array.from(orderedEnvelopes);
    const [removed] = reorderedEnvelopes.splice(source.index, 1);
    reorderedEnvelopes.splice(destination.index, 0, removed);

    setOrderedEnvelopes(reorderedEnvelopes);
  };

  const handleSave = async () => {
    const orderedEnvelopesIndices: EnvelopeOrderInput[] = orderedEnvelopes.map((envelope, index) => ({
      envelopeId: envelope.id,
      orderPosition: index,
    }));

    await updateEnvelopeOrder({
      variables: {
        input: {
          envelopes: orderedEnvelopesIndices,
        },
      },
    });
  };

  return (
    <Dialog>
      <Dialog.Trigger asChild={true}>
        <Button variant="textPrimary">Sort by</Button>
      </Dialog.Trigger>
      <Dialog.Content css={{ p: 'space.400' }} size="large">
        <Dialog.Title>
          <H2 variant="title300">Account Order</H2>
        </Dialog.Title>
        <Dialog.Description asChild={true}>
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="envelopes-list">
              {droppableProvided => (
                <Box
                  ref={droppableProvided.innerRef}
                  {...droppableProvided.droppableProps}
                  css={{ overflowY: 'scroll', maxHeight: '300px' }}
                >
                  {orderedEnvelopes.map((envelope, index: number) => (
                    <Draggable key={envelope.id} draggableId={envelope.id} index={index}>
                      {draggableProvided => (
                        <Box
                          ref={draggableProvided.innerRef}
                          {...draggableProvided.draggableProps}
                          {...draggableProvided.dragHandleProps}
                        >
                          <DraggableEnvelopeRow envelope={envelope} />
                        </Box>
                      )}
                    </Draggable>
                  ))}
                  {droppableProvided.placeholder}
                </Box>
              )}
            </Droppable>
            {envelopes.some(envelope =>
              envelope.accounts.some(account => account.__typename === 'CheckingAccount')
            ) && (
              <>
                <Separator />
                <Text as="p" color="text.weak" css={{ my: 'space.200' }}>
                  Checking and joint checking accounts cannot be sorted at this time.
                </Text>
              </>
            )}
          </DragDropContext>
        </Dialog.Description>
        <Dialog.Close onClick={handleSave}>Save</Dialog.Close>
      </Dialog.Content>
    </Dialog>
  );
};
